import React from 'react';
import { Form } from 'react-bootstrap';
import type { FormFieldProps } from '../../types/common';

export interface TextAreaProps extends FormFieldProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  maxLength?: number;
  autoFocus?: boolean;
  readOnly?: boolean;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  showCharacterCount?: boolean;
}

/**
 * TextArea component built on React Bootstrap
 * Provides multi-line text input with optional character counting
 */
export const TextArea: React.FC<TextAreaProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  rows = 3,
  maxLength,
  autoFocus = false,
  readOnly = false,
  resize = 'vertical',
  showCharacterCount = false,
  helpText,
  validation,
  className = '',
  id,
  'data-testid': testId,
  ...props
}) => {
  const fieldId = id || `field-${name}`;
  const hasError = validation?.errors && validation.errors.length > 0;
  const hasWarning = validation?.warnings && validation.warnings.length > 0;
  const characterCount = value?.length || 0;

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const textAreaStyle = {
    resize: resize,
  };

  return (
    <Form.Group className={`mb-3 ${className}`.trim()}>
      {label && (
        <Form.Label htmlFor={fieldId}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
      )}
      
      <Form.Control
        as="textarea"
        id={fieldId}
        name={name}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows}
        maxLength={maxLength}
        autoFocus={autoFocus}
        style={textAreaStyle}
        isInvalid={hasError}
        isValid={validation?.isValid === true && !hasError && !hasWarning}
        data-testid={testId}
        aria-describedby={helpText ? `${fieldId}-help` : undefined}
        {...props}
      />

      {hasError && (
        <Form.Control.Feedback type="invalid">
          {validation?.errors?.join(', ')}
        </Form.Control.Feedback>
      )}

      {hasWarning && !hasError && (
        <div className="form-text text-warning">
          {validation?.warnings?.join(', ')}
        </div>
      )}

      {(showCharacterCount || helpText) && !hasError && !hasWarning && (
        <div className="d-flex justify-content-between">
          {helpText && (
            <Form.Text id={`${fieldId}-help`} className="text-muted">
              {helpText}
            </Form.Text>
          )}
          {showCharacterCount && (
            <small className={`text-muted ${maxLength && characterCount > maxLength * 0.9 ? 'text-warning' : ''}`}>
              {characterCount}{maxLength ? `/${maxLength}` : ''} characters
            </small>
          )}
        </div>
      )}
    </Form.Group>
  );
};

export default TextArea;
